<template>
  <div class="container">
    <!-- Header -->
    <div class="d-flex justify-content-between align-items-center mb-4">
      <div>
        <h2 class="mb-1">Transfers</h2>
        <nav aria-label="breadcrumb">
          <ol class="breadcrumb">
            <li class="breadcrumb-item">
              <router-link :to="{ name: 'teams-list' }" class="text-decoration-none">
                Bereiche
              </router-link>
            </li>
            <li class="breadcrumb-item">
              <router-link :to="{ name: 'teams-detail', params: { id: $route.params.id } }" class="text-decoration-none">
                {{ selectedTeam?.name }}
              </router-link>
            </li>
            <li class="breadcrumb-item active" aria-current="page">Transfers</li>
          </ol>
        </nav>
      </div>
      <div class="d-flex gap-2">
        <button class="btn btn-outline-secondary" @click="goBack">
          <font-awesome-icon icon="arrow-left" /> Zurück
        </button>
      </div>
    </div>

    <!-- Team Quittungen Info -->
    <div class="row mb-4">
      <div class="col-md-6">
        <div class="card">
          <div class="card-body text-center">
            <h5 class="card-title">Team Quittungen</h5>
            <div class="display-4 text-warning mb-3">
              {{ selectedTeam?.account?.amount || 0 }}
            </div>
            <small class="text-muted">Verfügbare Quittungen im Team-Account</small>
            <div class="mt-3">
              <button class="btn btn-primary btn-sm" @click="openTransferDialog">
                <font-awesome-icon icon="exchange-alt" class="me-1" />
                Neuer Transfer
              </button>
            </div>
          </div>
        </div>
      </div>
      <div class="col-md-6">
        <div class="card">
          <div class="card-body text-center">
            <h5 class="card-title">Persönliche Quittungen</h5>
            <div class="display-4 text-primary mb-3">
              {{ personalCoins }}
            </div>
            <small class="text-muted">Ihre verfügbaren Quittungen</small>
            <div class="mt-3">
              <button class="btn btn-success btn-sm" @click="goToBuyCoins">
                <font-awesome-icon icon="shopping-cart" class="me-1" />
                Quittungen kaufen
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Transfer History -->
    <div class="card">
      <div class="card-header">
        <h5 class="mb-0">Transfer-Historie</h5>
      </div>
      <div class="card-body">
        <CoinTransferHistory 
          v-if="selectedTeam"
          :team-id="selectedTeam.id"
          :is-team-admin="isAdminOrCreator"
        />
      </div>
    </div>

    <!-- Transfer Dialog -->
    <CoinTransferDialog
      v-if="selectedTeam"
      :show="showTransferDialog"
      :team-id="selectedTeam.id"
      :personal-coins="personalCoins"
      :team-coins="selectedTeam.account?.amount || 0"
      @close="showTransferDialog = false"
      @transfer-completed="handleTransferCompleted"
    />
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { storeToRefs } from 'pinia'
import { showSuccess } from '@/services/notification'
import { useAuthStore } from '@/stores/auth'
import { useTeamsStore } from '@/stores/teams'
import { useSmartNavigation } from '@/services/navigation'
import CoinTransferHistory from '@/components/coins/CoinTransferHistory.vue'
import CoinTransferDialog from '@/components/coins/CoinTransferDialog.vue'

const router = useRouter()
const route = useRoute()
const teamsStore = useTeamsStore()
const authStore = useAuthStore()
const { goBackToTeam } = useSmartNavigation()

const { selectedTeam, loading } = storeToRefs(teamsStore)
const currentUser = computed(() => authStore.user)

const showTransferDialog = ref(false)
const personalCoins = ref(0)

const isAdminOrCreator = computed(() => {
  if (!selectedTeam.value || !currentUser.value) return false
  return (
    selectedTeam.value.role === 'teamAdmin' || 
    selectedTeam.value.creatorId === currentUser.value.id
  )
})

const openTransferDialog = async () => {

  if (currentUser.value?.account?.amount) {
    personalCoins.value = currentUser.value.account.amount
  } else {
    await authStore.updateProfile()
    personalCoins.value = authStore.user?.account?.amount || 0
  }
  
  showTransferDialog.value = true
}

const handleTransferCompleted = async (transferData) => {

  await teamsStore.loadTeamById(selectedTeam.value.id)

  await authStore.updateProfile()
  showSuccess(`Transfer von ${transferData.amount} Quittungen erfolgreich!`)
}

const goBack = () => {
  goBackToTeam(route.params.id)
}

const goToBuyCoins = () => {
  router.push({ name: 'account' })
}

onMounted(async () => {

  if (!selectedTeam.value || selectedTeam.value.id !== route.params.id) {
    await teamsStore.loadTeamById(route.params.id)
  }
  

  if (currentUser.value?.account?.amount) {
    personalCoins.value = currentUser.value.account.amount
  } else {
    await authStore.updateProfile()
    personalCoins.value = authStore.user?.account?.amount || 0
  }
})
</script>

